import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
  Skeleton,
  Button,
} from '@chakra-ui/react';
import React from 'react';
import { Link, Navigate, useSearchParams, useLocation } from 'react-router-dom';

import { useGetCurrentFundingRequestQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { Typography } from 'packages/common-frontend/components/Typography';
import { z } from 'zod/v4';
import { FundingRequestLandingViewContainer } from '~/borrower/domains/fundingRequests/ui/landing/ViewContainer';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { PermissionGuard, PermissionsFn } from '~/core/PermissionGuard';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { FundingRequestTemplateProviderV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { ActionsButton } from '~/packages/funding-requests/ui/submit/ActionsButton';
import {
  SubmitFundingRequest,
  SubmitFundingRequestForAlphaCustomers,
} from '~/packages/funding-requests/ui/submit/index';

// Types
const RouteParamsStruct = z.object({
  creditFacilityId: z.string({
    message: 'Missing credit facility ID query parameter',
  }),
  fundingRequestIdToDuplicate: z.string().optional(),
});

// Fragments
const RouteBase = () => {
  const { state: locationState } = useLocation();
  const flags = useFeatureFlags();

  const [queryParams] = useSearchParams();
  const {
    creditFacilityId,
    fundingRequestIdToDuplicate,
  } = RouteParamsStruct.parse(Object.fromEntries(queryParams));

  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacility = facilities.find(
    (facility) => facility.id === creditFacilityId,
  );

  const { data, loading: isLoading } = useGetCurrentFundingRequestQuery({
    fetchPolicy: 'no-cache',
    variables: { creditFacilityId },
  });

  if (isLoading) {
    return <Skeleton height="100vh" width="100%" />;
  }

  if (
    data?.currentFundingRequest &&
    data.currentFundingRequest.status !== 'pending'
  ) {
    return (
      <Navigate
        to={`/funding-requests/${data.currentFundingRequest.id}`}
        state={{ from: '/funding-requests/create' }}
      />
    );
  }

  if (
    data?.currentFundingRequest &&
    data.currentFundingRequest.status === 'pending' &&
    data.currentFundingRequest.type === 'upsizing'
  ) {
    return (
      <Flex direction="column" gap="1.5rem">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding Requests', path: '/funding-requests' },
            // @jendevelops TODO: update breadcrumb to include loan id
            { label: creditFacility?.name || 'Create Funding Request' },
          ]}
        />
        <Typography variant="page-title">Create Funding Request</Typography>

        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>
            A funding request for{' '}
            {creditFacility?.name || 'the selected facility'} has been
            submitted. The{' '}
            <Button
              variant="link"
              as={Link}
              to={`/funding-requests/${data.currentFundingRequest.id}`}
            >
              existing funding request
            </Button>{' '}
            must be approved before submitting a new one, or canceled to proceed
            with a new funding request.
          </AlertDescription>
        </Alert>
      </Flex>
    );
  }

  if (flags.displayFundingRequestV2ProviderV2) {
    return <p>Hi I am a placeholder for the new provider route for /create</p>;
  }

  if (flags.displayFundingRequestV2Alpha) {
    return (
      <FundingRequestTemplateProviderV1
        mode="create"
        allowedActions="mutable"
        creditFacilityId={creditFacilityId}
        fundingRequestIdToDuplicate={fundingRequestIdToDuplicate}
      >
        <Flex direction="column" gap="1.5rem">
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Funding Requests', path: '/funding-requests' },
              { label: creditFacility?.name || 'Create Funding Request' },
            ]}
          />

          <Typography variant="page-title">Create Funding Request</Typography>
          {/* TODO consolidate with non alpha, so can have download button */}
        </Flex>
        <SubmitFundingRequestForAlphaCustomers />
      </FundingRequestTemplateProviderV1>
    );
  }
  if (flags.displayFundingRequestV2) {
    return (
      <FundingRequestTemplateProviderV1
        mode="create"
        allowedActions="mutable"
        creditFacilityId={creditFacilityId}
        fundingRequestIdToDuplicate={fundingRequestIdToDuplicate}
      >
        <Flex direction="column" gap="1.5rem">
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Funding Requests', path: '/funding-requests' },
              { label: creditFacility?.name || 'Create Funding Request' },
            ]}
          />
          <Flex justify="space-between">
            <Typography variant="page-title">Create Funding Request</Typography>
            <ActionsButton />
          </Flex>
        </Flex>
        {locationState?.from &&
          locationState.from === 'resubmit funding request' &&
          fundingRequestIdToDuplicate && (
            <Alert status="info" mb="1.5rem">
              <AlertIcon />
              <AlertDescription>
                This funding request is a resubmission from Funding Request{' '}
                <Button
                  variant="link"
                  as={Link}
                  to={`/funding-requests/${fundingRequestIdToDuplicate}`}
                >
                  {fundingRequestIdToDuplicate}
                </Button>
              </AlertDescription>
            </Alert>
          )}
        {/* @jendevelops Update to get the type from context, since create route
      template provider is sometimes responsible for initing the FR if it doesn't exist */}
        <SubmitFundingRequest type="origination" />
      </FundingRequestTemplateProviderV1>
    );
  }

  return (
    <>
      <Flex direction="column" gap="1.5rem">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding Requests', path: '/funding-requests' },
            { label: creditFacility?.name || 'Create Funding Request' },
          ]}
        />
        <Typography variant="page-title">Create Funding Request</Typography>
      </Flex>
      <FundingRequestLandingViewContainer creditFacilityId={creditFacilityId} />
    </>
  );
};

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.userType === 'CAPITAL_PROVIDER') {
    return context.flags.productsFundingRequestsLenderCreatePermissions;
  }
  return context.flags.productsFundingRequestsCreatePermissions;
};

export const Route = () => (
  <PermissionGuard
    permissions={permissions}
    fallback={<Navigate to="/funding-requests" replace />}
  >
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/funding-requests" />;
      }}
    >
      <Flex
        direction="column"
        padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      >
        <RouteBase />
      </Flex>
    </RouteErrorBoundary>
  </PermissionGuard>
);
